import './FaqPage.css';
import { useState, useMemo, useEffect, useRef } from 'react';
import faqData from './faqData';

// normalizacja jak przy sprawdzaniu odpowiedzi w zagadkach: wielkość liter
// i polskie znaki nie mogą decydować o tym, czy gracz coś znajdzie
const normalize = (value) => String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l');

const stripHtml = (html) => String(html).replace(/<[^>]+>/g, ' ');

// kategoria ma albo podkategorie, albo pytania wprost - płaską listę pakujemy
// w jedną bezimienną grupę, żeby dalej wszystko renderowało się tak samo
const groupsOf = (category) => category.groups
    || [{ id: category.id, title: null, items: category.items }];

const FaqPage = ({ onClose }) => {
    const [query, setQuery] = useState('');
    const [openCategories, setOpenCategories] = useState([]);
    const [openGroups, setOpenGroups] = useState([]);
    const [openItems, setOpenItems] = useState([]);
    const panelRef = useRef(null);

    const term = normalize(query.trim());
    const isSearching = term.length > 0;

    // indeks budowany raz - pytanie, treść odpowiedzi, hasła dodatkowe i nazwy sekcji
    const index = useMemo(() => {
        const map = {};
        faqData.forEach(category => {
            groupsOf(category).forEach(group => {
                group.items.forEach((item, i) => {
                    map[`${category.id}-${group.id}-${i}`] = normalize(
                        `${category.title} ${group.title || ''} ${item.q} ${stripHtml(item.a)} ${item.keys || ''}`
                    );
                });
            });
        });
        return map;
    }, []);

    const categories = useMemo(() => {
        if (!isSearching) return faqData;

        return faqData
            .map(category => {
                const groups = groupsOf(category)
                    .map(group => ({
                        ...group,
                        items: group.items.filter(
                            (item, i) => index[`${category.id}-${group.id}-${i}`].includes(term)
                        )
                    }))
                    .filter(group => group.items.length > 0);

                return { ...category, groups, items: null };
            })
            .filter(category => category.groups.length > 0);
    }, [term, isSearching, index]);

    const foundCount = categories.reduce(
        (sum, category) => sum + groupsOf(category).reduce((n, group) => n + group.items.length, 0),
        0
    );

    // Escape zamyka FAQ - tak samo jak krzyżyk
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // klik poza panelem - czyli w górny pasek zakładek, bo tylko on zostaje
    // odsłonięty - zamyka FAQ, tak samo jak menu podpowiedzi
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (panelRef.current && !panelRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const toggle = (setter) => (id) => {
        setter(prev => prev.includes(id)
            ? prev.filter(el => el !== id)
            : [...prev, id]
        );
    };

    const toggleCategory = toggle(setOpenCategories);
    const toggleGroup = toggle(setOpenGroups);
    const toggleItem = toggle(setOpenItems);

    // przy aktywnym wyszukiwaniu wszystko jest rozwinięte, żeby gracz od razu
    // widział odpowiedź, a nie kolejne kliknięcia
    const isCategoryOpen = (id) => isSearching || openCategories.includes(id);
    const isGroupOpen = (id) => isSearching || openGroups.includes(id);
    const isItemOpen = (id) => isSearching || openItems.includes(id);

    const renderItem = (item, key, marker) => (
        <div className="faq__item" key={key}>
            <button
                className={`faq__question ${isItemOpen(key) ? '-open' : ''}`}
                onClick={() => toggleItem(key)}
                aria-expanded={isItemOpen(key)}
            >
                <span className="faq__marker" aria-hidden="true">{marker}</span>
                <span className="faq__question-text">{item.q}</span>
                <span className="faq__chevron" aria-hidden="true">▾</span>
            </button>

            {isItemOpen(key) && (
                <div
                    className="faq__answer"
                    dangerouslySetInnerHTML={{ __html: item.a }}
                />
            )}
        </div>
    );

    return (
        <div className="faq" ref={panelRef} role="dialog" aria-label="Pomoc w grze">

            <div className="faq__bar">
                <h2 className="faq__title">Pomoc w grze</h2>
                <button
                    className="faq__close-btn"
                    onClick={onClose}
                    aria-label="Zamknij pomoc"
                >
                    ✖
                </button>
            </div>

            <div className="faq__search">
                <input
                    className="faq__search-input"
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Szukaj, np. kamera, punkty, GPS"
                    autoComplete="off"
                />
                {isSearching && (
                    <div className="faq__search-info">
                        {foundCount === 0 ? 'Brak wyników' : `Znaleziono: ${foundCount}`}
                    </div>
                )}
            </div>

            <div className="faq__content">

                {categories.map(category => (
                    <div className="faq__category" key={category.id}>
                        <button
                            className={`faq__category-btn ${isCategoryOpen(category.id) ? '-open' : ''}`}
                            onClick={() => toggleCategory(category.id)}
                            aria-expanded={isCategoryOpen(category.id)}
                        >
                            <span className="faq__category-title">{category.title}</span>
                            <span className="faq__chevron" aria-hidden="true">▾</span>
                        </button>

                        {isCategoryOpen(category.id) && (
                            <div className="faq__groups">
                                {groupsOf(category).map((group, groupIndex) => {
                                    const groupKey = `${category.id}-${group.id}`;

                                    // płaska kategoria - pytania numerowane wprost
                                    if (!group.title) {
                                        return (
                                            <div className="faq__items -flat" key={groupKey}>
                                                {group.items.map((item, i) =>
                                                    renderItem(item, `${groupKey}-${i}`, `${i + 1}.`)
                                                )}
                                            </div>
                                        );
                                    }

                                    return (
                                        <div className="faq__group" key={groupKey}>
                                            <button
                                                className={`faq__group-btn ${isGroupOpen(groupKey) ? '-open' : ''}`}
                                                onClick={() => toggleGroup(groupKey)}
                                                aria-expanded={isGroupOpen(groupKey)}
                                            >
                                                <span className="faq__marker" aria-hidden="true">{groupIndex + 1}.</span>
                                                <span className="faq__group-title">{group.title}</span>
                                                <span className="faq__chevron" aria-hidden="true">▾</span>
                                            </button>

                                            {isGroupOpen(groupKey) && (
                                                <div className="faq__items">
                                                    {group.items.map((item, i) =>
                                                        renderItem(item, `${groupKey}-${i}`, '•')
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ))}

                {isSearching && foundCount === 0 && (
                    <div className="faq__empty">
                        Nie znaleźliśmy odpowiedzi na to hasło. Spróbujcie innego słowa albo
                        napiszcie na <a href="mailto:kontakt@secretgate.pl">kontakt@secretgate.pl</a>.
                    </div>
                )}

                <p className="faq__footer-note">
                    Nie ma tu Waszego pytania? Piszcie na{' '}
                    <a href="mailto:kontakt@secretgate.pl">kontakt@secretgate.pl</a>.
                </p>

            </div>
        </div>
    );
};

export default FaqPage;
